import React, { useEffect } from 'react';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';

import { featureToggleChanged } from '@/configuration/featureToggle/featureToggleSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { RootState, store } from '@/configuration/setup/store';
import { accessToken } from '@/configuration/tokenHandling/accessToken';
import { accessTokenStored } from '@/configuration/tokenHandling/tokenSlice';
import {
    assetsChanged,
    criticalityChanged,
    currentFiltersSelected,
    detailClosed,
    viewTypeChanged,
} from '@/features/batteryOverview/slices/batteryOverviewSlice';
import { isReadinessNotifiedSelector, readinessNotified } from './appSlice';

// Events sent
export const BATTERY_SERVICE_READY = 'BATTERY_SERVICE/READY';
export const BATTERY_SERVICE_ASSET_SELECTED = 'BATTERY_SERVICE/ASSET_SELECTED';
export const BATTERY_SERVICE_OPEN_REMOTE_DIALOG = 'BATTERY_SERVICE/OPEN_BATTERY_HISTORY_GRAPH_DIALOG';
export const OUT_NOTIFY_READINESS = 'MFE/BOOTSTRAP/MFE_IS_LOADED';
export const REQUEST_TOKEN_REFRESH = 'BATTERY_SERVICE/REQUEST_TOKEN_REFRESH';

// Events received from web-workshop
export const TOKEN_RENEWED = 'WEB_WORKSHOP/TOKEN_RENEWED';
export const IN_MSG_FEATURE_TOGGLE_CHANGED = 'WEB_WORKSHOP/FEATURE_TOGGLE_CHANGED';

// Events received from customer-frontend
export const CFE_ASSET_TREE_SELECTION_CHANGED = 'CFE/ASSET_TREE_SELECTION_CHANGED';
export const CFE_DIAGNOSTICS_CRITICALITY_CHANGED = 'CFE/DIAGNOSTICS_CRITICALITY_CHANGED';
export const CFE_DIAGNOSTICS_VIEW_TYPE_CHANGED = 'CFE/DIAGNOSTICS_VIEW_TYPE_CHANGED';
export const CFE_DIAGNOSTICS_DETAIL_CLOSED = 'CFE/DIAGNOSTICS_DETAIL_CLOSED';
export const CFE_CURRENT_FILTERS_SELECTED = 'CFE/CURRENT_FILTERS_SELECTED';
export const IN_MSG_CFE_FEATURE_TOGGLE_CHANGED = 'CFE/FEATURE_TOGGLE_CHANGED';

interface MessageEvent<T> {
    type: string;
    payload: T;
}

export const sendMessage = (message: MessageEvent<any>) => {
    const referrer = document.referrer;
    if (referrer) {
        window.parent.postMessage(message, referrer);
    }
};

const TOKEN_REFRESH_TIMEOUT_MS = 10000;

const waitForTokenRenewed = (): Promise<string> => {
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            window.removeEventListener('message', listener);
            reject(new Error('Token refresh request timed out'));
        }, TOKEN_REFRESH_TIMEOUT_MS);

        const expectedOrigin = document.referrer ? new URL(document.referrer).origin : null;

        const listener = (event: globalThis.MessageEvent) => {
            // Only accept messages from the expected parent origin
            if (expectedOrigin && event.origin !== expectedOrigin) {
                return;
            }
            const { type, payload } = event.data ?? {};
            if (type === TOKEN_RENEWED && payload?.token) {
                clearTimeout(timeout);
                window.removeEventListener('message', listener);
                resolve(payload.token);
            }
        };

        window.addEventListener('message', listener);
    });
};

/**
 * Requests a fresh token from the parent window (web-workshop).
 *
 * Sends a REQUEST_TOKEN_REFRESH message to the parent and waits for the
 * TOKEN_RENEWED response. The parent (web-workshop) already broadcasts
 * TOKEN_RENEWED to all MFE iframes when oidc-client renews the session.
 */
export const requestTokenRefresh = async (): Promise<string> => {
    sendMessage({ type: REQUEST_TOKEN_REFRESH, payload: { mfeName: 'battery-service' } });
    return await waitForTokenRenewed();
};

export const receiveMessage = (event: any, dispatchRemoteAction: ThunkDispatch<Dispatch<any>, RootState, any>) => {
    const { type, payload } = event.data;
    if (!type) {
        return;
    }

    switch (type) {
        case TOKEN_RENEWED:
            const { token } = event.data.payload;
            accessToken.saveAccessToken(token);
            dispatchRemoteAction(accessTokenStored(token));
            break;
        case CFE_ASSET_TREE_SELECTION_CHANGED: {
            dispatchRemoteAction(assetsChanged(payload));
            return;
        }
        case CFE_DIAGNOSTICS_CRITICALITY_CHANGED: {
            dispatchRemoteAction(criticalityChanged(payload));
            return;
        }
        case CFE_DIAGNOSTICS_VIEW_TYPE_CHANGED: {
            dispatchRemoteAction(viewTypeChanged(payload));
            return;
        }
        case CFE_DIAGNOSTICS_DETAIL_CLOSED: {
            dispatchRemoteAction(detailClosed());
            return;
        }
        case CFE_CURRENT_FILTERS_SELECTED: {
            dispatchRemoteAction(currentFiltersSelected(payload));
            return;
        }
        case IN_MSG_FEATURE_TOGGLE_CHANGED:
        case IN_MSG_CFE_FEATURE_TOGGLE_CHANGED:
            dispatchRemoteAction(
                featureToggleChanged({
                    featureKey: payload.key,
                    value: payload.enabled,
                }),
            );
            break;
        default: {
            console.warn('MessageHandler not implemented: ', type);
        }
    }
};

// eslint-disable-next-line react/display-name
export const MessageHandler = React.memo((props) => {
    const dispatch = useAppDispatch();

    const isReadinessNotified = useAppSelector(isReadinessNotifiedSelector);
    useEffect(() => {
        const listenerCallback = (event: any) => receiveMessage(event, dispatch);
        window.addEventListener('message', listenerCallback, false);

        if (!isReadinessNotified) {
            sendMessage({
                type: OUT_NOTIFY_READINESS,
                payload: {
                    mfeName: 'battery-service',
                },
            });

            dispatch(readinessNotified());
        }

        return () => window.removeEventListener('message', listenerCallback, false);
    }, []);

    return null;
});

export default MessageHandler;
